import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * Crave (chantier N, passe 2I) : le remplacement est SAUTÉ quand la galerie
   * re-rendue montre les mêmes médias dans le même ordre. Sans cette garde,
   * chaque sélection de taille remplaçait le nœud entier avec des <img> aux
   * `src` identiques : le navigateur les redécodait et la galerie clignotait.
   * La signature compare les `data-media-id` (l'identifiant Shopify du média,
   * insensible aux paramètres `?v=&width=` des URLs). Un produit dont la
   * variante change réellement les médias (image attachée, ordre différent)
   * garde le comportement natif.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.#mediaSignature(newMediaGallery) === this.#mediaSignature(this)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Ordered list of the media shown by a gallery, as a comparable string.
   * Falls back to query-stripped `src` for nodes without a `data-media-id`.
   *
   * @param {Element} gallery - A media-gallery element (live or parsed).
   * @returns {string}
   */
  #mediaSignature(gallery) {
    const nodes = gallery.querySelectorAll('[data-media-id], img[src]');
    return Array.from(nodes, (node) => {
      const id = node.getAttribute('data-media-id');
      if (id) return `id:${id}`;
      const src = node.getAttribute('src') || '';
      return `src:${src.split('?')[0]}`;
    }).join('|');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
